// Terrain System
// Renders and handles collision for level terrain loaded from JSON

import { VectorRenderer } from "../graphics/VectorRenderer";
import { Vec2 } from "../utils/types";
import { CONFIG } from "../config";
import {
  TerrainPath,
  LevelData,
  terrainPointsToVec2,
} from "../levels/LevelData";
import { lineSegmentsIntersect } from "../utils/math";

export interface TerrainSegment {
  start: Vec2;
  end: Vec2;
  type: string;
  color?: string;
}

export interface CollisionResult {
  collided: boolean;
  normal: Vec2; // Surface normal pointing away from terrain
  point: Vec2; // Collision point
}

export class Terrain {
  private segments: TerrainSegment[] = [];
  private paths: TerrainPath[] = [];
  private color: string;
  private levelWidth: number;
  private levelHeight: number;
  private screenWidth: number;
  private screenHeight: number;

  constructor(screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.levelWidth = screenWidth;
    this.levelHeight = screenHeight;
    this.color = CONFIG.colors.terrain.zone1;

    // Generate a simple default terrain for testing
    this.generateDefaultTerrain();
  }

  // Load terrain from level data
  loadFromLevelData(levelData: LevelData): void {
    this.paths = levelData.terrain;
    this.levelWidth = levelData.width;
    this.levelHeight = levelData.height;
    this.color = levelData.terrainColor || CONFIG.colors.terrain.zone1;

    // Convert paths to segments for collision detection
    this.segments = [];

    for (const path of this.paths) {
      const points = terrainPointsToVec2(path.points);
      const pathColor = path.color || this.color;

      for (let i = 0; i < points.length - 1; i++) {
        this.segments.push({
          start: points[i],
          end: points[i + 1],
          type: path.type || "ground",
          color: pathColor,
        });
      }

      // If closed, connect last point to first
      if (path.closed && points.length > 2) {
        this.segments.push({
          start: points[points.length - 1],
          end: points[0],
          type: path.type || "ground",
          color: pathColor,
        });
      }
    }

    // Add screen boundary walls (top and bottom edges of level)
    this.segments.push({
      start: { x: 0, y: 0 },
      end: { x: this.levelWidth, y: 0 },
      type: "boundary",
    });
    this.segments.push({
      start: { x: 0, y: this.levelHeight },
      end: { x: this.levelWidth, y: this.levelHeight },
      type: "boundary",
    });
  }

  // Generate simple default terrain for testing
  private generateDefaultTerrain(): void {
    const w = this.screenWidth;
    const h = this.screenHeight;

    // Simple ground and ceiling
    this.segments = [
      // Ground
      { start: { x: 0, y: h - 50 }, end: { x: w, y: h - 50 }, type: "ground" },
      // Ceiling
      { start: { x: 0, y: 50 }, end: { x: w, y: 50 }, type: "ceiling" },
      // Left wall
      { start: { x: 20, y: 50 }, end: { x: 20, y: h - 50 }, type: "wall" },
      // Right wall
      {
        start: { x: w - 20, y: 50 },
        end: { x: w - 20, y: h - 50 },
        type: "wall",
      },
    ];
  }

  // Reusable point objects to avoid per-frame allocations
  private screenStart = { x: 0, y: 0 };
  private screenEnd = { x: 0, y: 0 };
  // Reusable Map for color grouping to avoid per-frame allocations
  private colorGroups: Map<string, TerrainSegment[]> = new Map();

  // Render terrain with camera offset
  render(
    renderer: VectorRenderer,
    cameraX: number = 0,
    cameraY: number = 0,
  ): void {
    const margin = 100;

    // Clear arrays in existing Map (reuse Map to avoid allocations)
    for (const arr of this.colorGroups.values()) {
      arr.length = 0;
    }

    for (let i = 0; i < this.segments.length; i++) {
      const segment = this.segments[i];

      // Transform to screen coordinates
      const startX = segment.start.x - cameraX;
      const startY = segment.start.y - cameraY;
      const endX = segment.end.x - cameraX;
      const endY = segment.end.y - cameraY;

      // Culling - skip segments entirely off screen
      if (
        (startX < -margin && endX < -margin) ||
        (startX > this.screenWidth + margin &&
          endX > this.screenWidth + margin) ||
        (startY < -margin && endY < -margin) ||
        (startY > this.screenHeight + margin &&
          endY > this.screenHeight + margin)
      ) {
        continue;
      }

      // Group by color
      const color = segment.color || this.color;
      if (!this.colorGroups.has(color)) {
        this.colorGroups.set(color, []);
      }
      this.colorGroups.get(color)!.push(segment);
    }

    // Render each color group
    for (const [color, segments] of this.colorGroups) {
      renderer.setGlow(3, color);
      renderer.beginBatch();

      for (const segment of segments) {
        this.screenStart.x = segment.start.x - cameraX;
        this.screenStart.y = segment.start.y - cameraY;
        this.screenEnd.x = segment.end.x - cameraX;
        this.screenEnd.y = segment.end.y - cameraY;

        renderer.drawLine(this.screenStart, this.screenEnd, color, 2);
      }

      renderer.endBatch();
      renderer.clearGlow();
    }
  }

  // Check if a point collides with terrain (world coordinates)
  checkPointCollision(point: Vec2): boolean {
    // Check level bounds
    if (
      point.x < 0 ||
      point.x > this.levelWidth ||
      point.y < 0 ||
      point.y > this.levelHeight
    ) {
      return true;
    }

    // Check against terrain segments
    for (const segment of this.segments) {
      const dist = this.pointToSegmentDistance(
        point,
        segment.start,
        segment.end,
      );
      if (dist < 3) {
        return true;
      }
    }

    return false;
  }

  // Check if a line segment collides with any terrain (world coordinates)
  checkLineCollision(start: Vec2, end: Vec2): boolean {
    for (const segment of this.segments) {
      if (lineSegmentsIntersect(start, end, segment.start, segment.end)) {
        return true;
      }
    }
    return false;
  }

  // Check ship collision with terrain (world coordinates)
  checkShipCollision(
    shipPoints: Vec2[],
    shipCenter: Vec2,
    radius: number,
  ): boolean {
    return this.getShipCollision(shipPoints, shipCenter, radius).collided;
  }

  // Get detailed collision info including surface normal
  getShipCollision(
    shipPoints: Vec2[],
    shipCenter: Vec2,
    radius: number,
  ): CollisionResult {
    const noCollision: CollisionResult = {
      collided: false,
      normal: { x: 0, y: 0 },
      point: { x: 0, y: 0 },
    };

    // Quick bounds check - return normal pointing inward
    if (shipCenter.x - radius < 0) {
      return {
        collided: true,
        normal: { x: 1, y: 0 },
        point: { x: 0, y: shipCenter.y },
      };
    }
    if (shipCenter.x + radius > this.levelWidth) {
      return {
        collided: true,
        normal: { x: -1, y: 0 },
        point: { x: this.levelWidth, y: shipCenter.y },
      };
    }
    if (shipCenter.y - radius < 0) {
      return {
        collided: true,
        normal: { x: 0, y: 1 },
        point: { x: shipCenter.x, y: 0 },
      };
    }
    if (shipCenter.y + radius > this.levelHeight) {
      return {
        collided: true,
        normal: { x: 0, y: -1 },
        point: { x: shipCenter.x, y: this.levelHeight },
      };
    }

    // Check each ship point against terrain segments - find closest collision
    let closestDist = Infinity;
    let closestSegment: TerrainSegment | null = null;
    let closestPoint: Vec2 = { x: 0, y: 0 };

    for (const point of shipPoints) {
      for (const segment of this.segments) {
        const dist = this.pointToSegmentDistance(
          point,
          segment.start,
          segment.end,
        );
        if (dist < 2 && dist < closestDist) {
          closestDist = dist;
          closestSegment = segment;
          closestPoint = point;
        }
      }
    }

    // Check ship edges against terrain
    for (let i = 0; i < shipPoints.length; i++) {
      const nextI = (i + 1) % shipPoints.length;
      for (const segment of this.segments) {
        if (
          lineSegmentsIntersect(
            shipPoints[i],
            shipPoints[nextI],
            segment.start,
            segment.end,
          )
        ) {
          // Use midpoint of ship edge as collision point
          const midPoint = {
            x: (shipPoints[i].x + shipPoints[nextI].x) / 2,
            y: (shipPoints[i].y + shipPoints[nextI].y) / 2,
          };
          const dist = this.pointToSegmentDistance(
            midPoint,
            segment.start,
            segment.end,
          );
          if (dist < closestDist) {
            closestDist = dist;
            closestSegment = segment;
            closestPoint = midPoint;
          }
        }
      }
    }

    if (closestSegment) {
      // Calculate surface normal (perpendicular to segment)
      const dx = closestSegment.end.x - closestSegment.start.x;
      const dy = closestSegment.end.y - closestSegment.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);

      if (len > 0) {
        // Normal is perpendicular to the segment
        let nx = -dy / len;
        let ny = dx / len;

        // Make sure normal points away from terrain (toward ship center)
        const toShip = {
          x: shipCenter.x - closestPoint.x,
          y: shipCenter.y - closestPoint.y,
        };
        const dot = nx * toShip.x + ny * toShip.y;
        if (dot < 0) {
          nx = -nx;
          ny = -ny;
        }

        return {
          collided: true,
          normal: { x: nx, y: ny },
          point: closestPoint,
        };
      }
    }

    return noCollision;
  }

  // Distance from a point to a line segment
  private pointToSegmentDistance(
    point: Vec2,
    segStart: Vec2,
    segEnd: Vec2,
  ): number {
    const dx = segEnd.x - segStart.x;
    const dy = segEnd.y - segStart.y;
    const lenSq = dx * dx + dy * dy;

    if (lenSq === 0) {
      return Math.sqrt(
        (point.x - segStart.x) ** 2 + (point.y - segStart.y) ** 2,
      );
    }

    let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));

    const projX = segStart.x + t * dx;
    const projY = segStart.y + t * dy;

    return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
  }

  // Get segments for collision system
  getSegments(): TerrainSegment[] {
    return this.segments;
  }

  // Get level dimensions
  getLevelWidth(): number {
    return this.levelWidth;
  }

  getLevelHeight(): number {
    return this.levelHeight;
  }

  // Swept collision detection - checks if moving from prevPos to newPos would cross terrain
  // Returns the collision result with the first intersection point if collision occurs
  checkSweptCollision(
    prevPos: Vec2,
    newPos: Vec2,
    shipPoints: Vec2[],
    radius: number,
  ): { collision: CollisionResult; safePosition: Vec2 } {
    const noCollision = {
      collision: {
        collided: false,
        normal: { x: 0, y: 0 },
        point: { x: 0, y: 0 },
      },
      safePosition: newPos,
    };

    // Calculate movement vector
    const moveX = newPos.x - prevPos.x;
    const moveY = newPos.y - prevPos.y;
    const moveLen = Math.sqrt(moveX * moveX + moveY * moveY);

    // If not moving much, no need for swept detection
    if (moveLen < 1) {
      return noCollision;
    }

    // Check if the movement path of the center crosses any terrain
    let closestT = 1.0; // Parameter along movement (0 = prevPos, 1 = newPos)
    let closestSegment: TerrainSegment | null = null;
    let intersectionPoint: Vec2 | null = null;

    // Check center point path against terrain
    for (const segment of this.segments) {
      const result = this.lineIntersection(
        prevPos,
        newPos,
        segment.start,
        segment.end,
      );
      if (result && result.t < closestT) {
        closestT = result.t;
        closestSegment = segment;
        intersectionPoint = result.point;
      }
    }

    // Also check paths of ship corner points (transformed by movement)
    // This catches cases where the center passes through but corners would hit
    for (const shipPoint of shipPoints) {
      // Calculate this point's position relative to center
      const offsetX = shipPoint.x - newPos.x;
      const offsetY = shipPoint.y - newPos.y;

      // This point's previous and new positions
      const pointPrev = { x: prevPos.x + offsetX, y: prevPos.y + offsetY };
      const pointNew = { x: newPos.x + offsetX, y: newPos.y + offsetY };

      for (const segment of this.segments) {
        const result = this.lineIntersection(
          pointPrev,
          pointNew,
          segment.start,
          segment.end,
        );
        if (result && result.t < closestT) {
          closestT = result.t;
          closestSegment = segment;
          intersectionPoint = result.point;
        }
      }
    }

    // Check level bounds for swept collision
    // Left bound
    if (prevPos.x - radius >= 0 && newPos.x - radius < 0) {
      const t = (radius - prevPos.x) / (newPos.x - prevPos.x);
      if (t >= 0 && t < closestT) {
        closestT = t;
        closestSegment = null;
        intersectionPoint = { x: 0, y: prevPos.y + t * moveY };
        return {
          collision: {
            collided: true,
            normal: { x: 1, y: 0 },
            point: intersectionPoint,
          },
          safePosition: {
            x: prevPos.x + (closestT - 0.01) * moveX,
            y: prevPos.y + (closestT - 0.01) * moveY,
          },
        };
      }
    }
    // Right bound
    if (
      prevPos.x + radius <= this.levelWidth &&
      newPos.x + radius > this.levelWidth
    ) {
      const t = (this.levelWidth - radius - prevPos.x) / (newPos.x - prevPos.x);
      if (t >= 0 && t < closestT) {
        closestT = t;
        closestSegment = null;
        intersectionPoint = { x: this.levelWidth, y: prevPos.y + t * moveY };
        return {
          collision: {
            collided: true,
            normal: { x: -1, y: 0 },
            point: intersectionPoint,
          },
          safePosition: {
            x: prevPos.x + (closestT - 0.01) * moveX,
            y: prevPos.y + (closestT - 0.01) * moveY,
          },
        };
      }
    }
    // Top bound
    if (prevPos.y - radius >= 0 && newPos.y - radius < 0) {
      const t = (radius - prevPos.y) / (newPos.y - prevPos.y);
      if (t >= 0 && t < closestT) {
        closestT = t;
        closestSegment = null;
        intersectionPoint = { x: prevPos.x + t * moveX, y: 0 };
        return {
          collision: {
            collided: true,
            normal: { x: 0, y: 1 },
            point: intersectionPoint,
          },
          safePosition: {
            x: prevPos.x + (closestT - 0.01) * moveX,
            y: prevPos.y + (closestT - 0.01) * moveY,
          },
        };
      }
    }
    // Bottom bound
    if (
      prevPos.y + radius <= this.levelHeight &&
      newPos.y + radius > this.levelHeight
    ) {
      const t =
        (this.levelHeight - radius - prevPos.y) / (newPos.y - prevPos.y);
      if (t >= 0 && t < closestT) {
        closestT = t;
        closestSegment = null;
        intersectionPoint = { x: prevPos.x + t * moveX, y: this.levelHeight };
        return {
          collision: {
            collided: true,
            normal: { x: 0, y: -1 },
            point: intersectionPoint,
          },
          safePosition: {
            x: prevPos.x + (closestT - 0.01) * moveX,
            y: prevPos.y + (closestT - 0.01) * moveY,
          },
        };
      }
    }

    if (closestSegment && intersectionPoint) {
      // Calculate surface normal
      const dx = closestSegment.end.x - closestSegment.start.x;
      const dy = closestSegment.end.y - closestSegment.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);

      let nx = -dy / len;
      let ny = dx / len;

      // Make sure normal points in the direction we came from (opposite to movement)
      const dot = nx * moveX + ny * moveY;
      if (dot > 0) {
        nx = -nx;
        ny = -ny;
      }

      // Calculate safe position (just before collision)
      const safeT = Math.max(0, closestT - 0.01);
      const safePosition = {
        x: prevPos.x + safeT * moveX,
        y: prevPos.y + safeT * moveY,
      };

      return {
        collision: {
          collided: true,
          normal: { x: nx, y: ny },
          point: intersectionPoint,
        },
        safePosition,
      };
    }

    return noCollision;
  }

  // Line-line intersection helper
  // Returns intersection point and parameter t (0-1) along first line if they intersect
  private lineIntersection(
    p1: Vec2,
    p2: Vec2,
    p3: Vec2,
    p4: Vec2,
  ): { point: Vec2; t: number } | null {
    const d1x = p2.x - p1.x;
    const d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x;
    const d2y = p4.y - p3.y;

    const cross = d1x * d2y - d1y * d2x;

    // Lines are parallel
    if (Math.abs(cross) < 0.0001) {
      return null;
    }

    const dx = p3.x - p1.x;
    const dy = p3.y - p1.y;

    const t = (dx * d2y - dy * d2x) / cross;
    const u = (dx * d1y - dy * d1x) / cross;

    // Check if intersection is within both line segments
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        point: {
          x: p1.x + t * d1x,
          y: p1.y + t * d1y,
        },
        t,
      };
    }

    return null;
  }
}
